import { useEffect, useState } from "react";
import {
  ArrowsClockwise,
  CheckCircle,
  Sparkle,
  Image as ImageIcon,
} from "@phosphor-icons/react";
import api from "../lib/api";
import { confirmProductionWrite } from "../lib/diagnostics";
import { toast } from "sonner";

/**
 * Iter 20.19 — Odds + Image Enrichment card.
 *
 * Fetches detail pages for `source_pending_review` and (optionally)
 * `verified_live` records, runs deterministic odds + image extraction,
 * with an OpenAI gpt-4o-mini JSON fallback when `max_tickets` is still
 * missing.
 *
 * Provider routing (read-only here, surfaces in the diagnostic strip):
 *   preview → Emergent Universal Key
 *   prod    → OPENAI_API_KEY direct
 *   else    → disabled (records still get deterministic enrichment).
 *
 * Two-step protocol mirrors live-listing-ingest:
 *   1. Dry-run → returns `odds_enrichment_plan_id`.
 *   2. Apply → consumes stored plan; idempotent.
 *
 * NEVER changes `competition_data_status`. NEVER auto-promotes.
 */
export default function OddsEnrichmentCard({ diag, onApplied }) {
  const [operator, setOperator] = useState("");
  const [includePending, setIncludePending] = useState(true);
  const [includeVerified, setIncludeVerified] = useState(true);
  const [llmFallback, setLlmFallback] = useState(true);
  const [forceRefresh, setForceRefresh] = useState(false);
  const [limit, setLimit] = useState(40);
  const [llmCap, setLlmCap] = useState(20);
  const [busy, setBusy] = useState(null);
  const [plan, setPlan] = useState(null);
  const [provider, setProvider] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(
          "/admin/integrity/odds-enrichment/provider",
        );
        setProvider(data);
      } catch (e) {
        // Soft-fail — endpoint may be down; card still works without it.
        setProvider({ error: true });
      }
    })();
  }, []);

  const runDryRun = async () => {
    setBusy("dry-run");
    try {
      const { data } = await api.post("/admin/integrity/odds-enrichment", {
        dry_run: true,
        operator: operator || null,
        include_verified_live: includeVerified,
        include_source_pending_review: includePending,
        limit,
        llm_fallback: llmFallback,
        llm_call_cap: llmCap,
        force_refresh: forceRefresh,
      });
      setPlan(data);
      const s = data.summary || {};
      toast.success(
        `Odds enrichment dry-run: ${s.would_enrich || 0} would enrich · ` +
          `${s.max_tickets_found || 0} max_tickets found · ` +
          `${s.odds_recomputed || 0} recomputed · ` +
          `${s.still_odds_unavailable || 0} still unavailable.`,
      );
    } catch (e) {
      toast.error(
        "Odds enrichment dry-run failed: " +
          (e.response?.data?.detail?.error ||
            e.response?.data?.detail ||
            e.message),
      );
    } finally {
      setBusy(null);
    }
  };

  const runApply = async () => {
    if (!plan?.odds_enrichment_plan_id) return;
    const s = plan.summary || {};
    if ((s.would_enrich || 0) === 0) {
      toast("Nothing to apply — plan has 0 would_enrich.");
      return;
    }
    if (
      !confirmProductionWrite(
        diag,
        `Apply odds + image enrichment: ${s.would_enrich || 0} records ` +
          `(status preserved — no promotion, no demotion).`,
      )
    ) {
      return;
    }
    setBusy("apply");
    try {
      const { data } = await api.post("/admin/integrity/odds-enrichment", {
        dry_run: false,
        odds_enrichment_plan_id: plan.odds_enrichment_plan_id,
      });
      toast.success(
        `Odds enrichment applied: ${data.updated} updated · ` +
          `${data.odds_recomputed} scores recomputed · ` +
          `status_changed=${data.status_changed} · ` +
          `${data.skipped_already_applied} idempotent skips.`,
      );
      setPlan({ ...plan, last_apply: data });
      if (onApplied) onApplied();
    } catch (e) {
      const err =
        e.response?.data?.detail?.error ||
        e.response?.data?.detail ||
        e.message;
      toast.error("Odds enrichment apply failed: " + err);
    } finally {
      setBusy(null);
    }
  };

  const summary = plan?.summary || null;
  const llmDiag = plan?.llm_diag || null;
  const records = plan?.records || [];
  const providerInfo = provider?.provider || null;
  const providerLabel =
    providerInfo?.provider === "emergent"
      ? "Emergent Universal Key (preview)"
      : providerInfo?.provider === "openai"
        ? "OpenAI direct (production)"
        : providerInfo?.provider === "disabled"
          ? "LLM disabled (no key)"
          : "unknown";

  return (
    <div
      className="rounded-lg border border-[var(--cc-purple,#9333ea)]/30 bg-gradient-to-br from-[var(--cc-purple,#9333ea)]/[0.04] to-transparent p-5 space-y-4"
      data-testid="odds-enrichment-card"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Sparkle size={18} weight="bold" className="text-[var(--cc-gold)]" />
            <h3
              className="text-lg font-semibold text-white"
              data-testid="odds-enrichment-title"
            >
              Run Odds + Image Enrichment
            </h3>
          </div>
          <p className="text-xs text-white/60 max-w-2xl leading-relaxed">
            Fetches detail pages, extracts <strong>max_tickets</strong>,{" "}
            <strong>draw_date</strong>, and real prize images. Deterministic
            first (JSON-LD, <code>__NEXT_DATA__</code>, OpenGraph), then
            gpt-4o-mini JSON fallback when <code>max_tickets</code> is still
            missing. Status preserved — never promoted, never demoted.
          </p>
        </div>
        <div
          className="text-[10px] px-2 py-1 rounded border border-white/10 text-white/60"
          data-testid="odds-enrichment-provider"
        >
          Provider: <span className="text-[var(--cc-gold)]">{providerLabel}</span>
        </div>
      </div>

      {/* Options */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
        <label className="inline-flex items-center gap-2 text-white/80">
          <input
            type="checkbox"
            checked={includePending}
            onChange={(e) => setIncludePending(e.target.checked)}
            data-testid="odds-enrichment-include-pending"
          />
          Include <code className="text-[10px]">source_pending_review</code>
        </label>
        <label className="inline-flex items-center gap-2 text-white/80">
          <input
            type="checkbox"
            checked={includeVerified}
            onChange={(e) => setIncludeVerified(e.target.checked)}
            data-testid="odds-enrichment-include-verified"
          />
          Include <code className="text-[10px]">verified_live</code>
        </label>
        <label className="inline-flex items-center gap-2 text-white/80">
          <input
            type="checkbox"
            checked={llmFallback}
            onChange={(e) => setLlmFallback(e.target.checked)}
            data-testid="odds-enrichment-llm-toggle"
          />
          LLM fallback (gpt-4o-mini)
        </label>
        <label className="inline-flex items-center gap-2 text-white/80">
          <input
            type="checkbox"
            checked={forceRefresh}
            onChange={(e) => setForceRefresh(e.target.checked)}
            data-testid="odds-enrichment-force-refresh"
          />
          Force refresh
        </label>
        <label className="text-white/80">
          Operator:
          <input
            type="text"
            value={operator}
            placeholder="e.g. LLF Games (optional)"
            onChange={(e) => setOperator(e.target.value)}
            data-testid="odds-enrichment-operator-input"
            className="ml-2 px-2 py-1 rounded bg-white/5 border border-white/10 text-xs w-44"
          />
        </label>
        <label className="text-white/80">
          Limit:
          <input
            type="number"
            value={limit}
            min={1}
            max={200}
            onChange={(e) => setLimit(parseInt(e.target.value || "40", 10))}
            data-testid="odds-enrichment-limit-input"
            className="ml-2 px-2 py-1 rounded bg-white/5 border border-white/10 text-xs w-20"
          />
        </label>
        <label className="text-white/80">
          LLM cap:
          <input
            type="number"
            value={llmCap}
            min={0}
            max={100}
            onChange={(e) => setLlmCap(parseInt(e.target.value || "20", 10))}
            data-testid="odds-enrichment-llm-cap-input"
            className="ml-2 px-2 py-1 rounded bg-white/5 border border-white/10 text-xs w-20"
          />
        </label>
      </div>

      {/* Buttons */}
      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={runDryRun}
          disabled={busy != null}
          data-testid="odds-enrichment-dry-run-btn"
          className="btn-gold px-4 py-2 rounded text-xs inline-flex items-center gap-2 disabled:opacity-50"
        >
          <ArrowsClockwise size={14} weight="bold" />
          {busy === "dry-run" ? "Running dry-run…" : "Dry-run"}
        </button>
        <button
          onClick={runApply}
          disabled={busy != null || !plan?.odds_enrichment_plan_id}
          data-testid="odds-enrichment-apply-btn"
          className="px-4 py-2 rounded text-xs inline-flex items-center gap-2 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <CheckCircle size={14} weight="bold" />
          {busy === "apply" ? "Applying…" : "Apply plan"}
        </button>
        {plan?.odds_enrichment_plan_id && (
          <span
            className="text-[10px] text-white/50 font-mono"
            data-testid="odds-enrichment-plan-id"
          >
            plan {plan.odds_enrichment_plan_id.slice(0, 8)}
            {plan.plan_expires_at
              ? ` · expires ${new Date(plan.plan_expires_at).toLocaleTimeString("en-GB")}`
              : ""}
          </span>
        )}
      </div>

      {/* Summary */}
      {summary && (
        <div
          className="rounded-md bg-black/30 border border-white/10 p-3 space-y-3"
          data-testid="odds-enrichment-summary"
        >
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
            <Stat
              label="Scanned"
              value={summary.scanned}
              testId="odds-enrichment-scanned"
            />
            <Stat
              label="Would enrich"
              value={summary.would_enrich}
              testId="odds-enrichment-would-enrich"
              tone="ok"
            />
            <Stat
              label="Already complete"
              value={summary.skip_already_complete}
              testId="odds-enrichment-skip-complete"
              tone="muted"
            />
            <Stat
              label="Protected (admin-edited)"
              value={summary.skip_protected}
              testId="odds-enrichment-skip-protected"
              tone="warn"
            />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-[10px]">
            <Stat
              label="max_tickets found"
              value={summary.max_tickets_found}
              testId="odds-enrichment-max-tickets-found"
              tone="ok"
            />
            <Stat
              label="Odds recomputed"
              value={summary.odds_recomputed}
              testId="odds-enrichment-odds-recomputed"
              tone="ok"
            />
            <Stat
              label="Still unavailable"
              value={summary.still_odds_unavailable}
              testId="odds-enrichment-still-unavailable"
              tone="warn"
            />
            <Stat
              label="Status changed"
              value={summary.status_changed}
              testId="odds-enrichment-status-changed"
              tone={summary.status_changed === 0 ? "muted" : "bad"}
            />
            <Stat
              label="Admin overrides preserved"
              value={summary.admin_overrides_preserved}
              testId="odds-enrichment-overrides-preserved"
              tone="muted"
            />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[10px]">
            <Stat
              label="Pending · scanned"
              value={summary.counters_source_pending_review?.scanned}
              testId="odds-enrichment-counter-pending-scanned"
            />
            <Stat
              label="Pending · enrich"
              value={summary.counters_source_pending_review?.enrich_proposed}
              testId="odds-enrichment-counter-pending-enrich"
              tone="ok"
            />
            <Stat
              label="Verified · scanned"
              value={summary.counters_verified_live?.scanned}
              testId="odds-enrichment-counter-verified-scanned"
            />
            <Stat
              label="Verified · enrich"
              value={summary.counters_verified_live?.enrich_proposed}
              testId="odds-enrichment-counter-verified-enrich"
              tone="ok"
            />
          </div>
        </div>
      )}

      {/* Before / After sample */}
      {plan?.sample_before_after?.length > 0 && (
        <details
          className="rounded-md bg-emerald-500/5 border border-emerald-500/20 p-3"
          data-testid="odds-enrichment-before-after"
        >
          <summary className="cursor-pointer text-xs font-semibold text-white/80">
            Sample · before vs after ({plan.sample_before_after.length})
          </summary>
          <div className="mt-2 space-y-2 text-[11px]">
            {plan.sample_before_after.map((s) => (
              <div
                key={s.id}
                className="border-l-2 border-emerald-500/40 pl-2"
                data-testid={`odds-enrichment-ba-${s.id}`}
              >
                <div className="text-white/85 font-medium truncate">{s.title}</div>
                <div className="grid sm:grid-cols-2 gap-1 mt-0.5 text-white/65">
                  <div>
                    <span className="opacity-60">before:</span>{" "}
                    max=<strong>{s.before.max_tickets ?? "—"}</strong> ·
                    odds=<strong>{s.before.estimated_odds}</strong> ·
                    score=<strong>{s.before.overall_compcheck_score}</strong>
                    <div className="text-[10px] opacity-60">
                      flags: {(s.before.risk_flags || []).join(", ") || "—"}
                    </div>
                  </div>
                  <div>
                    <span className="opacity-60">after:</span>{" "}
                    max=<strong className="text-emerald-300">{s.after.max_tickets ?? "—"}</strong> ·
                    odds=<strong className="text-emerald-300">{s.after.estimated_odds}</strong> ·
                    score=<strong className="text-emerald-300">{s.after.overall_compcheck_score}</strong>
                    <div className="text-[10px] opacity-60">
                      flags: {(s.after.risk_flags || []).join(", ") || "—"}
                    </div>
                  </div>
                </div>
                {s.after.summary && (
                  <div className="mt-1 text-white/60 text-[10px] italic line-clamp-2">
                    {s.after.summary}
                  </div>
                )}
              </div>
            ))}
          </div>
        </details>
      )}

      {/* LLM diagnostics */}
      {llmDiag && (
        <div
          className="rounded-md bg-purple-500/5 border border-purple-500/20 p-3 text-[11px] text-white/70 space-y-1"
          data-testid="odds-enrichment-llm-diag"
        >
          <div className="font-semibold text-white/80">
            LLM diagnostics ·{" "}
            <span className="text-[var(--cc-gold)]">
              {llmDiag.provider_resolved?.provider}
            </span>{" "}
            · {llmDiag.provider_resolved?.model}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1">
            <div>
              Calls: <strong>{llmDiag.llm_calls_attempted}</strong> attempted,{" "}
              {llmDiag.llm_calls_succeeded} ok, {llmDiag.llm_calls_failed} failed
            </div>
            <div>
              Skipped:{" "}
              {llmDiag.llm_calls_skipped_no_key +
                llmDiag.llm_calls_skipped_cap}{" "}
              ({llmDiag.llm_calls_skipped_no_key} no-key,{" "}
              {llmDiag.llm_calls_skipped_cap} cap)
            </div>
            <div>Cache hits: {llmDiag.llm_cache_hits}</div>
            <div data-testid="odds-enrichment-llm-cost">
              Est. cost:{" "}
              <strong>${llmDiag.llm_estimated_cost_usd.toFixed(6)}</strong>
            </div>
            {llmDiag.llm_disabled_reason && (
              <div className="col-span-2 text-amber-200">
                Disabled: {llmDiag.llm_disabled_reason}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Records preview */}
      {records.length > 0 && (
        <details
          className="rounded border border-white/10 bg-white/[0.02] px-3 py-2"
          data-testid="odds-enrichment-records-details"
        >
          <summary className="cursor-pointer text-xs text-white/70 font-semibold">
            Records ({records.length})
          </summary>
          <ul className="mt-2 space-y-1.5 text-[11px]">
            {records.slice(0, 30).map((r) => (
              <li
                key={r.id}
                className="flex items-start gap-2 border-l-2 border-emerald-500/30 pl-2"
                data-testid={`odds-enrichment-record-${r.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-white/85 font-medium truncate">
                    {r.title || "(untitled)"}
                  </div>
                  <div className="text-white/50 text-[10px] truncate">
                    {r.detail_url} · {r.current_status}
                  </div>
                  <div className="text-white/60 text-[10px]">
                    <span className="text-emerald-300/80">
                      changed: {(r.changed_fields || []).join(", ") || "—"}
                    </span>
                    {r.llm_used && (
                      <span className="ml-2 px-1 py-0 rounded bg-purple-500/15 text-purple-200 text-[9px]">
                        LLM
                      </span>
                    )}
                    {r.image_source_layer &&
                      r.image_source_layer !== "none" && (
                        <span className="ml-2 inline-flex items-center gap-1 text-white/50">
                          <ImageIcon size={9} /> {r.image_source_layer}
                          {r.image_matches_prize === true && (
                            <span className="text-emerald-300/80">match</span>
                          )}
                          {r.image_matches_prize === false && (
                            <span className="text-amber-200/80">unmatched</span>
                          )}
                        </span>
                      )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </details>
      )}

      {plan?.last_apply && (
        <div
          className="rounded-md bg-emerald-500/10 border border-emerald-500/30 p-3 text-xs text-emerald-200"
          data-testid="odds-enrichment-last-apply"
        >
          Applied at{" "}
          {new Date(plan.last_apply.applied_at).toLocaleString("en-GB")}:{" "}
          {plan.last_apply.updated} updated · {plan.last_apply.odds_recomputed} scores recomputed ·{" "}
          status_changed={plan.last_apply.status_changed} ·{" "}
          {plan.last_apply.skipped_already_applied} idempotent skips.
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, testId, tone }) {
  const toneCls =
    tone === "ok"
      ? "bg-emerald-500/15 text-emerald-200"
      : tone === "warn"
        ? "bg-amber-500/15 text-amber-200"
        : tone === "bad"
          ? "bg-red-500/15 text-red-200"
          : tone === "muted"
            ? "bg-white/5 text-white/60"
            : "bg-white/5 text-white/80";
  return (
    <div className={`rounded px-2 py-1.5 ${toneCls}`} data-testid={testId}>
      <div className="text-[9px] uppercase tracking-wide opacity-70">
        {label}
      </div>
      <div className="text-sm font-semibold font-mono-num">{value ?? 0}</div>
    </div>
  );
}
